import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Card, ListGroup, Badge, Alert } from "react-bootstrap";
import { FaRedo, FaSearch, FaCheck, FaExclamationTriangle, FaGraduationCap } from "react-icons/fa";
import { getClassesBySchoolId, getStudentsBySchoolId, markStudentAsRepeat } from "../services/APIClient";

function RepeatingStudentsModal({ show, onHide, schoolid, teacherid, teacherName }) {
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Filter states
  const [studentGradeFilter, setStudentGradeFilter] = useState("all");
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [repeatReason, setRepeatReason] = useState("");
  const [repeatYear, setRepeatYear] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Available grade levels using boolean system
  const gradeLevels = [
    { value: "all", label: "All Grades" },
    { value: "grade_level1", label: "Junior Infants" },
    { value: "grade_level2", label: "Senior Infants" },
    { value: "grade_level3", label: "1st Class" },
    { value: "grade_level4", label: "2nd Class" },
    { value: "grade_level5", label: "3rd Class" },
    { value: "grade_level6", label: "4th Class" },
    { value: "grade_level7", label: "5th Class" },
    { value: "grade_level8", label: "6th Class" }
  ];

  const getGradeLabelFromClass = (classItem) => {
    if (!classItem) return "Unknown Grade";
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const getGradeBooleanFromClass = (classItem) => {
    if (!classItem) return "unknown";
    if (classItem.grade_level1) return "grade_level1";
    if (classItem.grade_level2) return "grade_level2";
    if (classItem.grade_level3) return "grade_level3";
    if (classItem.grade_level4) return "grade_level4";
    if (classItem.grade_level5) return "grade_level5";
    if (classItem.grade_level6) return "grade_level6";
    if (classItem.grade_level7) return "grade_level7";
    if (classItem.grade_level8) return "grade_level8";
    return "unknown";
  };

  // Generate academic year options
  const generateAcademicYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    
    // Generate 5 years back and 2 years forward from current year
    for (let i = currentYear - 5; i <= currentYear + 2; i++) {
      const academicYear = `${i}-${(i + 1).toString().slice(-2)}`;
      years.push({
        value: academicYear,
        label: `${i}/${(i + 1).toString().slice(-2)} Academic Year`
      });
    }
    
    return years.reverse(); // Most recent years first
  };

  useEffect(() => {
    if (show && schoolid) {
      loadStudents();
      // Set default to current academic year
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth(); // 0-based, so September = 8
      
      // If we're in September-December, we're in the first part of the academic year
      // If we're in January-August, we're in the second part of the academic year
      let academicYear;
      if (currentMonth >= 8) { // September onwards
        academicYear = `${currentYear}-${(currentYear + 1).toString().slice(-2)}`;
      } else { // January to August
        academicYear = `${currentYear - 1}-${currentYear.toString().slice(-2)}`;
      }
      
      setRepeatYear(academicYear);
    }
  }, [show, schoolid]);

  const loadStudents = async () => {
    setLoading(true);
    setError("");
    try {
      const studentsResponse = await getStudentsBySchoolId(schoolid);

      // Transform student data to match component expectations
      const transformedStudents = (studentsResponse || []).map(student => {
        const gradeLabel = student.class ? getGradeLabelFromClass(student.class) : 'Unknown Grade';
        const gradeBoolean = student.class ? getGradeBooleanFromClass(student.class) : 'unknown';
        
        // Check if student has repeat years
        const hasRepeatYears = student.repeatYears && student.repeatYears.length > 0;
        const repeatYearsList = hasRepeatYears ? student.repeatYears.map(entry => entry.year || entry) : [];
        
        return {
          id: student.id,
          name: student.name,
          firstName: student.firstName,
          lastName: student.lastName,
          classId: student.classId,
          className: student.class?.name || 'Unknown Class',
          grade: gradeLabel,
          gradeBoolean: gradeBoolean,
          isRepeating: hasRepeatYears,
          repeatYears: repeatYearsList,
          repeatDetails: student.repeatYears || [] // Keep full repeat details for display
        };
      }).sort((a, b) => {
        // Sort alphabetically by display name
        const nameA = (a.firstName && a.lastName) ? `${a.firstName} ${a.lastName}` : a.name || '';
        const nameB = (b.firstName && b.lastName) ? `${b.firstName} ${b.lastName}` : b.name || '';
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });

      setStudents(transformedStudents);

    } catch (err) {
      setError("Failed to load students. Please try again.");
      console.error("Error loading students:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudents(prev => {
      const isSelected = prev.find(s => s.id === student.id);
      if (isSelected) {
        return prev.filter(s => s.id !== student.id);
      } else {
        return [...prev, student];
      }
    });
  };

  const handleSelectAllStudents = () => {
    const filteredStudents = getFilteredStudents();
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents);
    }
  };

  const getFilteredStudents = () => {
    return students.filter(student => {
      const matchesGrade = studentGradeFilter === "all" || student.gradeBoolean === studentGradeFilter;
      const matchesSearch = student.name?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
                           student.firstName?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
                           student.lastName?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
                           student.className?.toLowerCase().includes(studentSearchTerm.toLowerCase());
      return matchesGrade && matchesSearch;
    });
  };

  const handleMarkAsRepeatingClick = () => {
    if (selectedStudents.length === 0) {
      setError("Please select at least one student to mark as repeating.");
      return;
    }
    if (!repeatReason.trim()) {
      setError("Please provide a reason for marking these students as repeating.");
      return;
    }
    if (!repeatYear.trim()) {
      setError("Please specify the academic year for which they will repeat.");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmMarkAsRepeating = async () => {
    setShowConfirmModal(false);
    await handleBulkMarkAsRepeating();
  };

  const handleBulkMarkAsRepeating = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Process each student individually since your API handles one at a time
      for (const student of selectedStudents) {
        try {
          await markStudentAsRepeat(
            student.id, 
            repeatYear.trim(), 
            teacherid?.toString() || "unknown", 
            teacherName || "Unknown Teacher",
            repeatReason.trim()
          );
          successCount++;
        } catch (err) {
          errorCount++;
          errors.push({
            studentId: student.id,
            studentName: student.firstName && student.lastName 
              ? `${student.firstName} ${student.lastName}` 
              : student.name,
            error: err.message || "Unknown error"
          });
          console.error(`Error marking student ${student.id} as repeating:`, err);
        }
      }

      if (successCount > 0) {
        setSuccess(`Successfully marked ${successCount} student(s) as repeating for ${repeatYear}.`);
        
        if (errorCount > 0) {
          setError(`Warning: ${errorCount} student(s) could not be processed. ${errors.map(e => `${e.studentName}: ${e.error}`).join(', ')}`);
        }
      } else {
        setError("No students were marked as repeating. Please try again.");
      }

      setSelectedStudents([]);
      setRepeatReason("");
      
      // Reload data to reflect changes
      await loadStudents();

    } catch (err) {
      setError("Failed to mark students as repeating. Please try again.");
      console.error("Mark as repeating error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStudents([]);
    setError("");
    setSuccess("");
    setStudentGradeFilter("all");
    setStudentSearchTerm("");
    setRepeatReason("");
    setRepeatYear("");
    setShowConfirmModal(false);
    onHide();
  };

  const autoClearMessage = () => {
    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 5000);
  };

  // Auto-clear messages
  useEffect(() => {
    if (error || success) {
      autoClearMessage();
    }
  }, [error, success]);

  const filteredStudents = getFilteredStudents();

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaRedo className="me-2 text-warning" />
            Mark Students as Repeating
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ minHeight: '500px' }}>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              Select Students to Mark as Repeating
              <Badge bg="warning" className="ms-2">{selectedStudents.length} selected</Badge>
            </h5>
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={handleSelectAllStudents}
              disabled={filteredStudents.length === 0}
            >
              {selectedStudents.length === filteredStudents.length ? "Deselect All" : "Select All"}
            </Button>
          </div>

          {/* Student Filters */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Select 
                value={studentGradeFilter} 
                onChange={(e) => setStudentGradeFilter(e.target.value)}
                size="sm"
              >
                {gradeLevels.map(grade => (
                  <option key={grade.value} value={grade.value}>
                    {grade.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Search students, classes..."
                  value={studentSearchTerm}
                  onChange={(e) => setStudentSearchTerm(e.target.value)}
                  size="sm"
                />
                <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
              </div>
            </Col>
          </Row>

          {/* Repeat Details */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-medium">
                  Academic Year <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={repeatYear}
                  onChange={(e) => setRepeatYear(e.target.value)}
                  className={!repeatYear.trim() && selectedStudents.length > 0 ? 'border-warning' : ''}
                >
                  <option value="">Select Academic Year</option>
                  {generateAcademicYearOptions().map(year => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  The academic year they will be repeating
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-medium">
                  Reason for Repeating <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Please provide a reason for marking these students as repeating..."
                  value={repeatReason}
                  onChange={(e) => setRepeatReason(e.target.value)}
                  className={!repeatReason.trim() && selectedStudents.length > 0 ? 'border-warning' : ''}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Students List */}
          <div style={{ height: '300px', overflowY: 'auto' }}>
            {loading ? (
              <div className="text-center py-4">Loading students...</div>
            ) : filteredStudents.length === 0 ? (
              <div className="text-center py-4 text-muted">No students found</div>
            ) : (
              <ListGroup>
                {filteredStudents.map(student => (
                  <ListGroup.Item
                    key={student.id}
                    className={`d-flex justify-content-between align-items-center cursor-pointer ${
                      selectedStudents.find(s => s.id === student.id) ? 'bg-warning-light border-warning' : ''
                    } ${student.isRepeating ? 'border-start border-warning border-3' : ''}`}
                    onClick={() => handleStudentSelect(student)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="flex-grow-1">
                      <div className="fw-medium d-flex align-items-center">
                        {student.firstName && student.lastName 
                          ? `${student.firstName} ${student.lastName}` 
                          : student.name}
                        {student.isRepeating && (
                          <Badge bg="warning" className="ms-2">
                            Repeating: {student.repeatYears.join(', ')}
                          </Badge>
                        )}
                      </div>
                      <small className="text-muted">
                        Class: {student.className} ({student.grade})
                      </small>
                      {student.isRepeating && student.repeatDetails.length > 0 && (
                        <div className="mt-1">
                          {student.repeatDetails.map((detail, index) => (
                            <small key={index} className="d-block text-warning">
                              <strong>{detail.year || detail}:</strong> {detail.reason || 'No reason provided'} 
                              {detail.dateMarked && ` (${new Date(detail.dateMarked).toLocaleDateString()})`}
                            </small>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedStudents.find(s => s.id === student.id) && (
                      <FaCheck className="text-warning ms-2" />
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div className="text-muted">
              {selectedStudents.length > 0 && (
                <span>
                  <FaRedo className="me-1 text-warning" />
                  {selectedStudents.length} student(s) selected for repeating
                </span>
              )}
            </div>
            <div>
              <Button variant="secondary" onClick={handleClose} className="me-2">
                Cancel
              </Button>
              <Button 
                variant="warning" 
                onClick={handleMarkAsRepeatingClick}
                disabled={selectedStudents.length === 0 || !repeatReason.trim() || !repeatYear.trim() || loading}
              >
                {loading ? "Processing..." : `Mark ${selectedStudents.length > 0 ? selectedStudents.length : ''} Student${selectedStudents.length !== 1 ? 's' : ''} as Repeating`}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title className="d-flex align-items-center">
            <FaExclamationTriangle className="me-2" />
            Confirm Mark Students as Repeating
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div className="bg-warning-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                 style={{ width: '60px', height: '60px' }}>
              <FaRedo className="text-warning" size={24} />
            </div>
            <h5 className="mb-2 text-warning">Mark {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''} as Repeating</h5>
            <p className="text-muted mb-0">These students will be marked as repeating for academic year {repeatYear}.</p>
          </div>

          {/* Students List */}
          <div className="mb-4">
            <h6 className="mb-3 d-flex align-items-center">
              <Badge bg="warning" className="me-2">{selectedStudents.length}</Badge>
              Students to be marked as repeating
            </h6>
            <div className="border rounded-3 bg-light p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {selectedStudents.map((student, index) => (
                <div key={student.id} className="d-flex justify-content-between align-items-center py-2 px-2 mb-2 bg-white rounded border-start border-warning border-3" 
                     style={{ borderStartWidth: '4px !important' }}>
                  <div>
                    <div className="fw-medium text-dark">
                      {student.firstName && student.lastName 
                        ? `${student.firstName} ${student.lastName}` 
                        : student.name}
                    </div>
                    <small className="text-muted">Class: {student.className}</small>
                  </div>
                  <Badge bg="outline-secondary" className="text-muted">{student.grade}</Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Repeat Details */}
          <div className="rounded-3 p-4 mb-4" 
               style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
            <div className="row">
              <div className="col-12">
                <div className="mb-3">
                  <strong>Reason for Repeating:</strong>
                  <div className="mt-1 p-2 bg-white rounded border">
                    {repeatReason || "No reason provided"}
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <small className="text-muted">Academic Year:</small>
                    <div className="fw-medium">{repeatYear}</div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted">Date Marked:</small>
                    <div className="fw-medium">{new Date().toLocaleDateString()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="alert alert-warning d-flex align-items-start" role="alert">
            <div className="me-2">ℹ️</div>
            <div>
              <strong>Important:</strong> This action will mark the selected students as repeating for the specified academic year. 
              This information will be recorded for tracking and reporting purposes.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light border-0 p-4">
          <div className="d-flex w-100 justify-content-between align-items-center">
            <div className="text-muted">
              <small>
                <strong>{selectedStudents.length}</strong> student{selectedStudents.length !== 1 ? 's' : ''} will be marked as repeating
              </small>
            </div>
            <div>
              <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)} className="me-3">
                Cancel
              </Button>
              <Button variant="warning" onClick={handleConfirmMarkAsRepeating} size="lg" className="px-4">
                <FaGraduationCap className="me-2" />
                Confirm Mark as Repeating
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default RepeatingStudentsModal;